import React from "react";

import HighlightPanel from "@helsenorge/designsystem-react/components/HighlightPanel";
import { createDateString_deadline } from "../../utils/date-utils";
import StatusDot from "@helsenorge/designsystem-react/components/StatusDot";
import {
  Duolist,
  DuolistGroup,
} from "@helsenorge/designsystem-react/components/Duolist";
import { useGetResources } from "../../hooks/useGetResources";
import { useAppSelector } from "../../store/reducer";
import { OppgaveStatus } from "../../enums/oppgaveStatus";
import {
  getStatusText,
  getStatusDotVariant,
  getOppgaveInstruksjon,
  getSkjemaSvarContent,
} from "../../utils/details-utils.tsx";
import { OppgaveSvarPDF } from "./OppgaveSvarPDF";

import styles from "../../styles/oppgave-detaljer.module.scss";
import { openSkjemaSvarLink } from "utils/pdf-utils.ts";
import { Intensjon } from "enums/intensjon.ts";

export const DetaljerPanel = (): React.JSX.Element | null => {
  const oppgave = useAppSelector((state) => state.oppgaveDetaljer.oppgave);
  const resources = useGetResources();
  const [showPdf, setShowPdf] = React.useState(false);

  if (!oppgave) {
    return null;
  }

  const handleSeSkjemaSvar = () => {
    if (oppgave.OppgaveSvar?.MimeType === "application/pdf") {
      setShowPdf(true);
    } else if (oppgave.OppgaveSvar?.MimeType === "text/html") {
      const htmlLink = openSkjemaSvarLink(
        oppgave.OppgaveSvar.Id || null,
        oppgave.OppgaveSvar.MimeType,
      );
      window.open(htmlLink, "_blank");
    }
  };

  const skjemaSvarContent = getSkjemaSvarContent(oppgave, handleSeSkjemaSvar);
  const visSkjemaSvar =
    oppgave.Status === OppgaveStatus.Completed && skjemaSvarContent;
  const visInstruksjon: boolean | undefined = !oppgave?.IntegrertVisning;
  const visObligatoriskTekst =
    oppgave.Intensjon === Intensjon.Plan &&
    (oppgave.Status === OppgaveStatus.Ready ||
      oppgave.Status === OppgaveStatus.InProgress);
  const showFullfortDato =
    oppgave.Status === OppgaveStatus.Cancelled ||
    oppgave.Status === OppgaveStatus.Completed;
  const showFristDato = !showFullfortDato;

  return (
    <>
      <HighlightPanel
        className={`${styles.highlightpanel} ${styles.detaljer_seksjon}`}
        color="blueberry"
      >
        <Duolist
          useCollapsedFromAndBelowBreakpoint="xs"
          className={styles.duolist}
        >
          <DuolistGroup
            description={
              <StatusDot
                text={getStatusText(
                  oppgave.Status,
                  resources,
                  oppgave.StatusBegrunnelse,
                  oppgave.FullfortDato,
                )}
                variant={getStatusDotVariant(oppgave.Status)}
                className={`${styles.statusDot} ${oppgave.Status === OppgaveStatus.Ready ? styles.statusDot_ready : ""}`}
              ></StatusDot>
            }
            term="Status:"
          />
          {showFristDato && (
            <DuolistGroup
              term={resources?.label_frist_ny || ""}
              description={createDateString_deadline(resources, oppgave.Frist)}
            ></DuolistGroup>
          )}
          <DuolistGroup
            term={resources?.label_avsender || ""}
            description={oppgave.KontaktNavn}
          ></DuolistGroup>
          {visInstruksjon && (
            <DuolistGroup
              term={resources?.label_oppgave || ""}
              description={getOppgaveInstruksjon(oppgave.Type, resources)}
            ></DuolistGroup>
          )}
          {oppgave.Begrunnelse && (
            <DuolistGroup
              term={resources?.label_begrunnelse || ""}
              description={oppgave.Begrunnelse}
            ></DuolistGroup>
          )}
          {visSkjemaSvar && (
            <DuolistGroup
              term={resources?.se_skjemasvar || ""}
              description={skjemaSvarContent}
            ></DuolistGroup>
          )}
        </Duolist>
        {visObligatoriskTekst && (
          <div className={styles.viktig_tekst}>
            {resources?.planDescription}
          </div>
        )}
      </HighlightPanel>
      <OppgaveSvarPDF showPdf={showPdf} setShowPdf={setShowPdf} />
    </>
  );
};
